import './lib/polyfills'
import { useEffect, useState } from 'react'

import { loadableReady } from '@loadable/component'
import axios from 'axios'
import { hydrate, render } from 'react-dom'
import { dehydrate as RqDehydrate } from 'react-query'
import { BrowserRouter } from 'react-router-dom'

import { AuthToken } from '@tribeplatform/gql-client/types'
import { QueryClient } from '@tribeplatform/react-sdk/lib'
import {
  getAuthMemberKey,
  getAuthTokenKey,
  getNetworkKey,
} from '@tribeplatform/react-sdk/utils/keys'
import { DocumentContext } from '@tribeplatform/react-ui-kit/hooks'
import { SpinnerIcon } from '@tribeplatform/react-ui-kit/icons'

import { AppInitialProps } from './@types'
import { App } from './App'
import { RuntimeConfigs } from './config'
import { APP_DATA_KEY, APP_ROOT_ELEMENT_ID } from './constants/app.constants'
import { redirectIfLoggedIn } from './lib/middleware/loginRedirect'
import { privateNetworkGuard } from './lib/middleware/privateNetworkGuard'
import { unconfirmedUsersRedirect } from './lib/middleware/unconfirmedUsersRedirect'
import { initSentry } from './lib/sentry/init'
import { getDomainInfo } from './utils/domain'
import { localize } from './utils/localize'
import { fetchTranslationData } from './utils/translation/fetchTranslationData'
import { I18nStore } from './utils/translation/I18nStore'

const getAppData = async ({ subfolder }): Promise<AppInitialProps> => {
  try {
    // FORCED_NETWORK_DOMAIN can be truthy only within dev environments
    const networkDomain =
      RuntimeConfigs.FORCED_NETWORK_DOMAIN || window.location.hostname
    const response = await axios.post<AuthToken>(
      `${subfolder || ''}/api/auth/refresh-token`,
      {
        networkDomain,
      },
    )

    if (!response?.data) {
      throw new Error('Something went wrong')
    }

    const authToken = response.data
    const queryClient = new QueryClient()
    queryClient.setQueryData(getAuthTokenKey({ fields: 'default' }), authToken)
    if (authToken.network) {
      queryClient.setQueryData(
        getNetworkKey({ fields: 'default' }),
        authToken.network,
      )
    }
    if (authToken.member) {
      queryClient.setQueryData(
        getAuthMemberKey({ fields: 'default' }),
        authToken.member,
      )
    }

    const dehydratedState = RqDehydrate(queryClient)

    return {
      url: {
        hostname: window.location.hostname,
        originalUrl: window.location.href,
        path: window.location.pathname,
        protocol: window.location.protocol,
        params: {},
      },
      authToken: response.data,
      dehydratedState,
    } as any
  } catch (e) {
    logger.warn('Could not fetch the auth token', e)
  }
}

const Client = ({ appData: initialAppData }) => {
  const [appData, setAppData] = useState<AppInitialProps>(initialAppData)

  const { subfolder } = getDomainInfo(appData?.authToken?.networkPublicInfo)

  useEffect(() => {
    getAppData({ subfolder }).then(res => {
      const { member, role, networkPublicInfo } = res?.authToken || {}
      if (
        redirectIfLoggedIn(role?.type) ||
        unconfirmedUsersRedirect(member, role) ||
        privateNetworkGuard(role?.type, networkPublicInfo?.visibility)
      ) {
        return
      }
      setAppData({ ...appData, ...res })
    })
  }, [])

  if (!appData?.authToken) {
    return (
      <div className="flex items-center justify-center h-screen w-screen">
        <SpinnerIcon className="animate-spin h-7 w-7" />
      </div>
    )
  }

  return (
    <BrowserRouter basename={subfolder}>
      <App pageProps={appData} apiUrl={RuntimeConfigs.TRIBE_GQL_ENDPOINT} />
    </BrowserRouter>
  )
}

const initApp = async () => {
  const appDataScript = document.getElementById(APP_DATA_KEY)
  let appData: AppInitialProps

  try {
    appData = JSON.parse(appDataScript.innerHTML)
    appDataScript.remove()
  } catch (e) {
    appData = undefined
  }

  const isSSR = !!appData?.dehydratedState
  initSentry(appData?.authToken)

  const { lang } = document.documentElement
  await localize(lang)
  const translationData = await fetchTranslationData(lang, appData)
  appData = {
    ...appData,
    i18n: I18nStore.getOrCreate(lang, translationData),
  }

  if (isSSR) {
    const { subfolder } = getDomainInfo(appData?.authToken?.networkPublicInfo)
    // Load all components needed before rendering
    loadableReady().then(() => {
      hydrate(
        <DocumentContext.Provider value={{ document }}>
          <BrowserRouter basename={subfolder}>
            <App
              pageProps={appData}
              apiUrl={RuntimeConfigs.TRIBE_GQL_ENDPOINT}
            />
          </BrowserRouter>
        </DocumentContext.Provider>,
        document.getElementById(APP_ROOT_ELEMENT_ID),
      )
    })
  } else {
    render(
      <DocumentContext.Provider value={{ document }}>
        <Client appData={appData} />
      </DocumentContext.Provider>,
      document.getElementById(APP_ROOT_ELEMENT_ID),
    )
  }
}

initApp()

if (module.hot) {
  module.hot.accept()
}
